import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { Coins, ChevronDown, ChevronUp, AlertTriangle, Flame, ChevronRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TALER_HINT_DISMISS_KEY = 'bb_taler_hint_dismissed';

const NANOUK_STATE_IMG = {
    fit: '/nanouk/energy_fit.png',
    balanced: '/nanouk/energy_balanced.png',
    light: '/nanouk/energy_light.png',
    heavy: '/nanouk/energy_heavy.png',
    crushed: '/nanouk/energy_crushed.png',
};

export function TalerCard({ date, refreshKey = 0 }) {
    const { language } = useLanguage();
    const de = language === 'de';
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [hintDismissed, setHintDismissed] = useState(() => localStorage.getItem(TALER_HINT_DISMISS_KEY) === '1');

    useEffect(() => {
        const dateStr = date ? date.toISOString().split('T')[0] : undefined;
        axios.get(`${API}/taler/today`, { params: { date: dateStr } })
            .then(res => setData(res.data))
            .catch(() => setData(null));
    }, [date, refreshKey]);

    if (!data?.enabled) {
        // Discoverability hint: Taler is opt-in — point users to Settings once
        if (!data || hintDismissed) return null;
        return (
            <Card className="border-dashed border-amber-300 bg-amber-50/40" data-testid="taler-activation-hint">
                <CardContent className="p-3 flex items-center gap-2.5">
                    <Coins className="w-5 h-5 text-amber-600 shrink-0" />
                    <button
                        className="flex-1 min-w-0 text-left"
                        onClick={() => navigate('/settings')}
                        data-testid="taler-hint-link"
                    >
                        <p className="text-sm font-semibold flex items-center gap-1">
                            {de ? 'Neu: Taler-System' : 'New: Taler system'}
                            <ChevronRight className="w-4 h-4 text-amber-600" />
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {de
                                ? 'Punkte statt Kalorien zählen – in den Einstellungen aktivieren.'
                                : 'Count points instead of calories – enable it in Settings.'}
                        </p>
                    </button>
                    <button
                        onClick={() => { localStorage.setItem(TALER_HINT_DISMISS_KEY, '1'); setHintDismissed(true); }}
                        className="p-1 text-muted-foreground hover:text-foreground shrink-0"
                        data-testid="taler-hint-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </CardContent>
            </Card>
        );
    }

    const { budget, sport_bonus, total_budget, spent, remaining, items, surplus_kcal } = data;
    const over = remaining < 0;
    const depleted = remaining === 0 && spent > 0;
    const warning = remaining > 0 && remaining <= 3 && spent > 0;
    const overdrawn = Math.abs(Math.min(0, remaining));
    const energyOver = (surplus_kcal ?? 0) > 0;

    // Daily Nanouk: reacts instantly to TODAY's taler status
    const nanoukState = over
        ? (overdrawn >= 5 ? 'crushed' : 'heavy')
        : depleted
            ? 'light'
            : warning
                ? 'balanced'
                : spent > 0
                    ? 'fit'
                    : 'balanced';

    // Coin row: spent = greyed out, remaining = gold (orange-pulse when low), overdrawn = red debt coins
    const coins = [];
    for (let i = 0; i < total_budget; i++) {
        coins.push(i < spent ? 'spent' : (warning ? 'warn' : 'free'));
    }
    for (let i = 0; i < Math.min(overdrawn, 12); i++) {
        coins.push('debt');
    }

    const cardClass = over
        ? 'border-2 border-red-400 bg-red-50 animate-taler-shake'
        : depleted
            ? 'border-2 border-orange-400 bg-orange-50'
            : warning
                ? 'border-orange-300 bg-orange-50/60'
                : 'border-amber-200 bg-amber-50/50';

    return (
        <Card className={cardClass} data-testid="taler-card">
            <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img
                            src={NANOUK_STATE_IMG[nanoukState]}
                            alt="Nanouk"
                            className={`w-11 h-11 object-contain shrink-0 ${over ? 'animate-pulse' : ''}`}
                            data-testid={`taler-nanouk-${nanoukState}`}
                        />
                        {over
                            ? <AlertTriangle className="w-5 h-5 text-red-600" />
                            : <Coins className={`w-5 h-5 ${depleted || warning ? 'text-orange-600' : 'text-amber-600'}`} />}
                        <span className={`text-sm font-bold ${over ? 'text-red-700 uppercase tracking-wide' : 'font-semibold'}`} data-testid="taler-card-title">
                            {over
                                ? (de ? 'Konto überzogen!' : 'Account overdrawn!')
                                : (de ? 'Taler heute' : 'Taler today')}
                        </span>
                    </div>
                    <span className={`text-lg font-bold ${over ? 'text-red-600' : depleted || warning ? 'text-orange-600' : 'text-amber-700'}`} data-testid="taler-remaining">
                        {over ? `−${overdrawn}` : `${remaining} / ${total_budget}`}
                    </span>
                </div>

                <div className="flex flex-wrap gap-1 items-center" data-testid="taler-coin-row">
                    {coins.slice(0, 44).map((state, i) => {
                        if (state === 'debt') {
                            return (
                                <div key={i} className="w-4 h-4 rounded-full bg-red-500 border border-red-600 flex items-center justify-center animate-pulse shadow-sm" data-testid="taler-debt-coin">
                                    <span className="text-[9px] font-bold text-white leading-none">−</span>
                                </div>
                            );
                        }
                        return (
                            <div key={i} className={`w-4 h-4 rounded-full border transition-colors ${state === 'spent'
                                ? 'bg-slate-200 border-slate-300'
                                : state === 'warn'
                                    ? 'bg-orange-400 border-orange-500 shadow-sm animate-pulse'
                                    : 'bg-amber-400 border-amber-500 shadow-sm'}`}
                            />
                        );
                    })}
                    {overdrawn > 12 && <span className="text-xs font-bold text-red-600">+{overdrawn - 12}</span>}
                </div>

                {/* Escalation messages */}
                {over && (
                    <div className="flex items-center gap-2.5 rounded-xl bg-red-100 border border-red-300 px-2.5 py-2" data-testid="taler-overdrawn-warning">
                        <p className="text-xs text-red-800 leading-snug">
                            {energyOver
                                ? (de
                                    ? <><strong>{overdrawn} Taler Schulden</strong> – du liegst <strong>+{surplus_kcal.toLocaleString('de-CH')} kcal</strong> über deinem Tagesziel 🧈 dazu Qualitäts-Aufschlag (viel Kohlenhydrate/Fett, wenig Protein). Nur Sport holt dich heute noch raus!</>
                                    : <><strong>{overdrawn} Taler debt</strong> – you're <strong>+{surplus_kcal.toLocaleString('en-GB')} kcal</strong> over your daily goal 🧈 plus a quality surcharge (lots of carbs/fat, little protein). Only sport can bail you out today!</>)
                                : (de
                                    ? <><strong>{overdrawn} Taler Schulden</strong> – kalorisch bist du im Rahmen! Die Schulden kommen von der <strong>Qualität</strong>: wenig Protein & Ballaststoffe. Mehr davon macht deine Mahlzeiten günstiger 🥦</>
                                    : <><strong>{overdrawn} Taler debt</strong> – calorie-wise you're fine! The debt comes from <strong>food quality</strong>: little protein & fiber. More of those makes your meals cheaper 🥦</>)}
                        </p>
                    </div>
                )}
                {depleted && !over && (
                    <div className="flex items-center gap-2 rounded-xl bg-orange-100 border border-orange-300 px-2.5 py-1.5" data-testid="taler-depleted-warning">
                        <Flame className="w-4 h-4 text-orange-600 shrink-0" />
                        <p className="text-xs text-orange-800 font-medium">
                            {de ? 'Budget aufgebraucht – ab jetzt zählt jeder Bissen!' : 'Budget used up – every bite counts from now on!'}
                        </p>
                    </div>
                )}
                {warning && (
                    <p className="text-xs text-orange-700 font-medium" data-testid="taler-low-warning">
                        {de ? `⚠️ Nur noch ${remaining} Taler – wähle weise!` : `⚠️ Only ${remaining} Taler left – choose wisely!`}
                    </p>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                        {de ? 'Budget' : 'Budget'} {budget}
                        {sport_bonus > 0 && <span className="text-green-600 font-medium"> +{sport_bonus} {de ? 'Sport' : 'sport'}</span>}
                        {' · '}{spent} {de ? 'verbraucht' : 'spent'}
                    </span>
                    {items?.length > 0 && (
                        <button onClick={() => setOpen(!open)} className={`flex items-center gap-0.5 font-medium ${over ? 'text-red-700' : 'text-amber-700'}`} data-testid="taler-toggle-details">
                            {de ? 'Details' : 'Details'}
                            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                    )}
                </div>

                {open && (
                    <div className={`pt-1 space-y-1 border-t ${over ? 'border-red-200' : 'border-amber-200/60'}`} data-testid="taler-details-list">
                        {items.map((it) => (
                            <div key={it.id} className="flex items-center justify-between text-xs">
                                <span className="truncate mr-2">{it.name}</span>
                                <span className="shrink-0 flex items-center gap-1.5">
                                    {it.quality_taler > 0 && (
                                        <span className="text-[10px] text-orange-600 font-medium" data-testid="taler-quality-surcharge">
                                            +{it.quality_taler} {de ? 'Qualität' : 'quality'}
                                        </span>
                                    )}
                                    {it.quality_taler < 0 && (
                                        <span className="text-[10px] text-green-600 font-medium" data-testid="taler-quality-discount">
                                            {it.quality_taler} {de ? 'Rabatt' : 'discount'}
                                        </span>
                                    )}
                                    <span className={`font-semibold ${over ? 'text-red-700' : 'text-amber-700'}`}>{it.taler} T</span>
                                </span>
                            </div>
                        ))}
                        <p className="text-[10px] text-muted-foreground pt-0.5">
                            {de
                                ? 'Basis: 100 kcal = 1 Taler · Qualität = Aufschlag für Kohlenhydrate/Fett, Rabatt für Protein/Ballaststoffe'
                                : 'Base: 100 kcal = 1 Taler · quality = surcharge for carbs/fat, discount for protein/fiber'}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
